import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI } from '../styles/theme';
import API from '../api';
import BackButton from './common/BackButton';
import MifidSettings from './settings/MifidSettings';
import FatcaSettings from './settings/FatcaSettings';
import PrivacySettings from './settings/PrivacySettings';

// ── Styles ────────────────────────────────────────────────────────────────────
const cardStyle = {
  backgroundColor: SEMANTIC.backgroundCard,
  borderRadius: UI.borderRadius.md,
  padding: UI.spacing.xl,
  marginBottom: UI.spacing.xl,
  boxShadow: UI.shadow.card,
};

const sectionTitleStyle = {
  color: SEMANTIC.actionBuy,
  fontSize: UI.fontSize.lg,
  fontWeight: UI.fontWeight.bold,
  marginBottom: UI.spacing.lg,
  paddingBottom: UI.spacing.sm,
  borderBottom: '1px solid #333',
};

const fieldRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${UI.spacing.sm} 0`,
  borderBottom: '1px solid #222',
};

const labelStyle = {
  color: SEMANTIC.textSecondary,
  fontSize: UI.fontSize.sm,
  minWidth: '140px',
};

const valueStyle = {
  color: SEMANTIC.textPrimary,
  fontSize: UI.fontSize.md,
  flex: 1,
  marginLeft: UI.spacing.md,
};

const inputStyle = {
  flex: 1,
  marginLeft: UI.spacing.md,
  padding: `${UI.spacing.sm} ${UI.spacing.md}`,
  backgroundColor: '#333',
  color: SEMANTIC.textPrimary,
  border: '1px solid #555',
  borderRadius: UI.borderRadius.sm,
  fontSize: UI.fontSize.md,
};

const editBtnStyle = {
  background: 'transparent',
  border: `1px solid ${SEMANTIC.actionBuy}`,
  color: SEMANTIC.actionBuy,
  borderRadius: UI.borderRadius.sm,
  padding: '4px 10px',
  fontSize: UI.fontSize.sm,
  cursor: 'pointer',
  marginLeft: UI.spacing.sm,
};

const saveBtnStyle = {
  background: SEMANTIC.ctaPrimary,
  border: 'none',
  color: SEMANTIC.ctaPrimaryText,
  borderRadius: UI.borderRadius.sm,
  padding: '4px 12px',
  fontSize: UI.fontSize.sm,
  fontWeight: UI.fontWeight.bold,
  cursor: 'pointer',
  marginLeft: UI.spacing.sm,
};

const cancelBtnStyle = {
  background: 'transparent',
  border: '1px solid #555',
  color: SEMANTIC.textSecondary,
  borderRadius: UI.borderRadius.sm,
  padding: '4px 10px',
  fontSize: UI.fontSize.sm,
  cursor: 'pointer',
  marginLeft: UI.spacing.sm,
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function MyData() {
  const { t } = useTranslation();
  const userId = localStorage.getItem('userId');

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState('');

  const [contractInfo, setContractInfo] = useState(null);
  const [contractLoading, setContractLoading] = useState(true);
  const [docAvailability, setDocAvailability] = useState({});

  const [editing, setEditing] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  useEffect(() => {
    if (!userId) return;
    API.get(`/users/${userId}/profile`)
      .then(res => setProfile(res.data))
      .catch(() => setProfileError(t('settings.error_load_profile')))
      .finally(() => setProfileLoading(false));
    API.get(`/users/${userId}/contract`)
      .then(res => setContractInfo(res.data))
      .catch(() => setContractInfo(null))
      .finally(() => setContractLoading(false));
    // Verifica disponibilità documenti statici
    ['service_guide', 'terms_conditions', 'transparency'].forEach(docType => {
      API.get(`/document/${docType}`)
        .then(() => setDocAvailability(prev => ({ ...prev, [docType]: true })))
        .catch(() => setDocAvailability(prev => ({ ...prev, [docType]: false })));
    });
  }, [userId, t]);

  const val = (v) => v || <span style={{ color: '#555' }}>{t('settings.not_set')}</span>;

  const ReadRow = ({ label, value }) => (
    <div style={fieldRowStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{val(value)}</span>
    </div>
  );

  const startEdit = (section) => {
    setSaveError('');
    setSaveSuccess('');
    if (section === 'email')   setEditVals({ email: profile?.email || '' });
    if (section === 'phone')   setEditVals({ phone: profile?.phone || '' });
    if (section === 'address') setEditVals({
      residence_address:  profile?.residence_address  || '',
      residence_city:     profile?.residence_city     || '',
      residence_province: profile?.residence_province || '',
      residence_zip:      profile?.residence_zip      || '',
    });
    setEditing(section);
  };

  const cancelEdit = () => { setEditing(null); setEditVals({}); setSaveError(''); };

  const saveEdit = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await API.put(`/users/${userId}/profile`, editVals);
      setProfile(prev => ({ ...prev, ...editVals }));
      setSaveSuccess(t('settings.success_update'));
      setEditing(null);
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (err) {
      setSaveError(err.response?.data?.error || t('settings.error_update_profile'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundApp, minHeight: '100vh', padding: UI.spacing.lg }}>
      <BackButton />
      <h2 style={{
        color: SEMANTIC.textPrimary, textAlign: 'center',
        marginBottom: UI.spacing.xl,
        fontSize: UI.fontSize.title, fontWeight: UI.fontWeight.bold,
      }}>
        {t('my_data.title')}
      </h2>

      {saveSuccess && (
        <div style={{
          backgroundColor: 'rgba(0,255,136,0.1)',
          border: `1px solid ${SEMANTIC.actionBuy}`,
          borderRadius: UI.borderRadius.sm,
          padding: UI.spacing.md,
          marginBottom: UI.spacing.lg,
          color: SEMANTIC.actionBuy,
          textAlign: 'center',
        }}>
          {saveSuccess}
        </div>
      )}

      {/* ── DATI PERSONALI ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>{t('settings.section_profile')}</h3>
        {profileLoading ? (
          <p style={{ color: SEMANTIC.textSecondary }}>{t('settings.loading_profile')}</p>
        ) : profileError ? (
          <p style={{ color: SEMANTIC.actionSell }}>{profileError}</p>
        ) : (
          <>
            {saveError && (
              <div style={{ color: SEMANTIC.actionSell, marginBottom: UI.spacing.md, fontSize: UI.fontSize.sm }}>
                {saveError}
              </div>
            )}

            {/* Dati anagrafici (read-only) */}
            <ReadRow label={t('settings.field_first_name')}   value={profile?.first_name} />
            <ReadRow label={t('settings.field_last_name')}    value={profile?.last_name} />
            <ReadRow label={t('settings.field_birth_date')}   value={profile?.birth_date} />
            <ReadRow label={t('settings.field_birth_city')}   value={profile?.birth_city} />
            <ReadRow label={t('settings.field_birth_province')} value={profile?.birth_province} />
            <ReadRow label={t('settings.field_tax_code')}     value={profile?.tax_code} />
            <ReadRow label={t('settings.field_citizenship')}  value={profile?.citizenship} />

            <div style={{ margin: `${UI.spacing.lg} 0 ${UI.spacing.md}`, borderTop: '1px solid #333' }} />

            {/* Email */}
            <div style={fieldRowStyle}>
              <span style={labelStyle}>{t('settings.field_email')}</span>
              {editing === 'email' ? (
                <>
                  <input style={inputStyle} type="email" value={editVals.email}
                    onChange={e => setEditVals({ email: e.target.value })} />
                  <button style={saveBtnStyle} onClick={saveEdit} disabled={saving}>{t('common.save')}</button>
                  <button style={cancelBtnStyle} onClick={cancelEdit}>{t('common.cancel')}</button>
                </>
              ) : (
                <>
                  <span style={valueStyle}>{val(profile?.email)}</span>
                  <button style={editBtnStyle} onClick={() => startEdit('email')}>
                    <i className="bi bi-pencil" /> {t('common.edit')}
                  </button>
                </>
              )}
            </div>

            {/* Telefono */}
            <div style={fieldRowStyle}>
              <span style={labelStyle}>{t('settings.field_phone')}</span>
              {editing === 'phone' ? (
                <>
                  <input style={inputStyle} type="tel" value={editVals.phone}
                    onChange={e => setEditVals({ phone: e.target.value })} />
                  <button style={saveBtnStyle} onClick={saveEdit} disabled={saving}>{t('common.save')}</button>
                  <button style={cancelBtnStyle} onClick={cancelEdit}>{t('common.cancel')}</button>
                </>
              ) : (
                <>
                  <span style={valueStyle}>{val(profile?.phone)}</span>
                  <button style={editBtnStyle} onClick={() => startEdit('phone')}>
                    <i className="bi bi-pencil" /> {t('common.edit')}
                  </button>
                </>
              )}
            </div>

            {/* Indirizzo */}
            <div style={{ paddingTop: UI.spacing.sm, borderBottom: '1px solid #222' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: UI.spacing.sm }}>
                <span style={{ ...labelStyle, fontWeight: UI.fontWeight.bold }}>{t('settings.field_address')}</span>
                {editing !== 'address' && (
                  <button style={editBtnStyle} onClick={() => startEdit('address')}>
                    <i className="bi bi-pencil" /> {t('common.edit')}
                  </button>
                )}
              </div>
              {editing === 'address' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: UI.spacing.sm, marginBottom: UI.spacing.md }}>
                  <input style={{ ...inputStyle, marginLeft: 0 }} placeholder={t('settings.field_address')}
                    value={editVals.residence_address}
                    onChange={e => setEditVals(v => ({ ...v, residence_address: e.target.value }))} />
                  <input style={{ ...inputStyle, marginLeft: 0 }} placeholder={t('settings.field_city')}
                    value={editVals.residence_city}
                    onChange={e => setEditVals(v => ({ ...v, residence_city: e.target.value }))} />
                  <div style={{ display: 'flex', gap: UI.spacing.sm }}>
                    <input style={{ ...inputStyle, marginLeft: 0, maxWidth: '80px' }} placeholder={t('settings.field_province')}
                      maxLength="2" value={editVals.residence_province}
                      onChange={e => setEditVals(v => ({ ...v, residence_province: e.target.value }))} />
                    <input style={{ ...inputStyle, marginLeft: 0, maxWidth: '100px' }} placeholder={t('settings.field_zip')}
                      maxLength="5" value={editVals.residence_zip}
                      onChange={e => setEditVals(v => ({ ...v, residence_zip: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', gap: UI.spacing.sm }}>
                    <button style={saveBtnStyle} onClick={saveEdit} disabled={saving}>{t('common.save')}</button>
                    <button style={cancelBtnStyle} onClick={cancelEdit}>{t('common.cancel')}</button>
                  </div>
                </div>
              ) : (
                <div style={{ paddingBottom: UI.spacing.sm }}>
                  <p style={{ color: SEMANTIC.textPrimary, margin: 0 }}>{val(profile?.residence_address)}</p>
                  {profile?.residence_city && (
                    <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, margin: 0 }}>
                      {profile.residence_city}
                      {profile.residence_province ? ` (${profile.residence_province})` : ''}
                      {profile.residence_zip ? ` – ${profile.residence_zip}` : ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── MIFID ── */}
      <MifidSettings userId={userId} />

      {/* ── FATCA ── */}
      <FatcaSettings userId={userId} />

      {/* ── PRIVACY ── */}
      <PrivacySettings userId={userId} />

      {/* ── DOCUMENTI ── */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>{t('my_data.section_documents')}</h3>
        {[
          {
            icon: 'bi-file-earmark-text',
            label: t('my_data.doc_contract'),
            sub: contractLoading
              ? null
              : contractInfo?.contract_signed_at
                ? t('my_data.doc_contract_signed_on', { date: contractInfo.contract_signed_at })
                : t('my_data.contract_not_signed'),
            viewUrl:     contractInfo?.has_pdf ? `/api/users/${userId}/contract/pdf` : null,
            downloadUrl: contractInfo?.has_pdf ? `/api/users/${userId}/contract/pdf` : null,
            downloadName: 'contratto_kuesto.pdf',
            unavailable: !contractLoading && (!contractInfo?.has_pdf),
          },
          {
            icon: 'bi-book',
            label: t('my_data.doc_service_guide'),
            viewUrl: docAvailability.service_guide ? '/api/document/service_guide/render' : null,
            unavailable: docAvailability.service_guide === false,
          },
          {
            icon: 'bi-file-earmark-ruled',
            label: t('my_data.doc_terms'),
            viewUrl: docAvailability.terms_conditions ? '/api/document/terms_conditions/render' : null,
            unavailable: docAvailability.terms_conditions === false,
          },
          {
            icon: 'bi-shield-check',
            label: t('my_data.doc_transparency'),
            viewUrl: docAvailability.transparency ? '/api/document/transparency/render' : null,
            comingSoon: docAvailability.transparency === false,
          },
        ].map((doc, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: `${UI.spacing.md} 0`,
            borderBottom: i < 3 ? '1px solid #222' : 'none',
            gap: UI.spacing.md,
            flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: UI.spacing.md, flex: 1 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                backgroundColor: 'rgba(0,255,136,0.07)',
                border: '1px solid rgba(0,255,136,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <i className={`bi ${doc.icon}`} style={{ color: SEMANTIC.actionBuy, fontSize: 16 }} />
              </div>
              <div>
                <div style={{ color: SEMANTIC.textPrimary, fontSize: UI.fontSize.md, fontWeight: UI.fontWeight.bold }}>{doc.label}</div>
                {doc.sub && <div style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, marginTop: 2 }}>{doc.sub}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: UI.spacing.sm, alignItems: 'center', flexShrink: 0 }}>
              {doc.comingSoon && (
                <span style={{ fontSize: UI.fontSize.sm, color: '#ffaa00', border: '1px solid rgba(255,170,0,0.4)', borderRadius: 6, padding: '3px 10px' }}>
                  {t('my_data.doc_coming_soon')}
                </span>
              )}
              {doc.unavailable && !doc.comingSoon && (
                <span style={{ fontSize: UI.fontSize.sm, color: SEMANTIC.textSecondary }}>
                  {t('my_data.doc_not_available')}
                </span>
              )}
              {doc.viewUrl && (
                <a href={doc.viewUrl} target="_blank" rel="noopener noreferrer" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 14px', borderRadius: UI.borderRadius.sm,
                  background: SEMANTIC.ctaPrimary, color: SEMANTIC.ctaPrimaryText,
                  fontSize: UI.fontSize.sm, fontWeight: UI.fontWeight.bold, textDecoration: 'none',
                }}>
                  <i className="bi bi-eye" /> {t('my_data.doc_view')}
                </a>
              )}
              {doc.downloadUrl && (
                <a href={doc.downloadUrl} download={doc.downloadName} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '6px 14px', borderRadius: UI.borderRadius.sm,
                  background: 'transparent', border: `1px solid ${SEMANTIC.actionBuy}`,
                  color: SEMANTIC.actionBuy,
                  fontSize: UI.fontSize.sm, fontWeight: UI.fontWeight.bold, textDecoration: 'none',
                }}>
                  <i className="bi bi-download" /> {t('my_data.doc_download')}
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
